import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import styles from "./loginnew.module.scss";

type OTPInputProps = {
  onChange: (value: string) => void;
  value?: string;
  length?: number;
};

const OTPInput = ({ onChange, value = "", length = 6 }: OTPInputProps) => {
  const [digits, setDigits] = useState<string[]>(
    Array.from({ length }, (_, index) => value[index] || ""),
  );
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    const sanitizedValue = value.replace(/\D/g, "").slice(0, length);
    setDigits(Array.from({ length }, (_, index) => sanitizedValue[index] || ""));
  }, [length, value]);

  const updateDigits = (nextDigits: string[]) => {
    setDigits(nextDigits);
    onChange(nextDigits.join(""));
  };

  const focusInput = (index: number) => {
    const nextInput = inputRefs.current[index];
    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  };

  const handleChange = (index: number, rawValue: string) => {
    const sanitizedValue = rawValue.replace(/\D/g, "");
    const nextDigits = [...digits];

    if (!sanitizedValue) {
      nextDigits[index] = "";
      updateDigits(nextDigits);
      return;
    }

    sanitizedValue
      .slice(0, length - index)
      .split("")
      .forEach((char, offset) => {
        nextDigits[index + offset] = char;
      });

    updateDigits(nextDigits);

    const nextIndex = Math.min(index + sanitizedValue.length, length - 1);
    focusInput(nextIndex);
  };

  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      focusInput(index - 1);
      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
      return;
    }

    if (event.key === "ArrowRight" && index < length - 1) {
      event.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedValue = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);

    if (!pastedValue) return;

    const nextDigits = Array.from(
      { length },
      (_, index) => pastedValue[index] || "",
    );
    updateDigits(nextDigits);

    const nextIndex = Math.min(pastedValue.length, length - 1);
    focusInput(nextIndex);
  };

  return (
    <div className={styles.otpInputContainer}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          className={styles.otpInputBox}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={length}
          value={digit}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          aria-label={`OTP digit ${index + 1}`}
        />
      ))}
    </div>
  );
};

export default OTPInput;
